import { JsonDecoder } from 'ts.data.json';

export interface Decor {
    label: string;
    decorId: string;
    imageUri: string;
}

interface Property {
    label: string;
    value: string;
}

export interface Insulation {
    articleNumber: string;
    label: string;
    imageUri: string | null;
    properties: Property[];
    filter: AccessoryFilter;
    squareMetersPerPackage: number;
    pricePerPackage: number;
    suitableFloorFamilies: string[];
    ean: string;
}

export interface Skirting {
    label: string;
    imageUri: string | null;
    skirtingId: string;
    dimensions: {
        width: number;
        height: number;
        length: number;
    };
    decors: SkirtingDecor[];
}

export interface SkirtingDecor {
    articleNumber: string;
    decorNumbers: string[];
    ean: string;
    imageUri: string | null;
    label: string;
    pricePerPiece: number;
    isMono: boolean;
}

export interface InstallationAndCare {
    label: string;
    imageUri: string | null;
    accessoryId: string;
    variants: InstallationAndCareVariant[];
    suitableFloorFamilies: string[];
}
interface InstallationAndCareVariant {
    articleNumber: string;
    ean: string;
    imageUri: string | null;
    label: string;
    pricePerPiece: number;
}

export type Subsoil = 'wood' | 'screed' | 'tiles' | 'other';
export type Requirement =
    | 'unevenSubsoil'
    | 'soundproofing'
    | 'thermalInsulation'
    | 'underfloorHeating'
    | 'vaporBarrier'
    | 'stress';

interface AccessoryFilter {
    subsoil: Subsoil[];
    requirements: Requirement[];
}

interface AccessoriesDataResponse {
    data: AccessoriesData;
}

export interface AccessoriesData {
    insulations: Insulation[];
    skirtings: Skirting[];
    installationAndCare: InstallationAndCare[];
}

export function fetchAccessories(locale: string): Promise<AccessoriesData> {
    return fetch('/api/shopping-planner/accessories', {
        headers: {
            'Accept-Language': locale,
            credentials: 'include',
        },
    })
        .then((response) => response.json())
        .then((json) => accessoriesDataResponseDecoder.decodeToPromise(json))
        .then((response) => response.data);
}

const insulationsDecoder = JsonDecoder.array<Insulation>(
    JsonDecoder.object(
        {
            articleNumber: JsonDecoder.string,
            label: JsonDecoder.string,
            imageUri: JsonDecoder.nullable(JsonDecoder.string),
            squareMetersPerPackage: JsonDecoder.number,
            pricePerPackage: JsonDecoder.number,
            suitableFloorFamilies: JsonDecoder.array(JsonDecoder.string, 'suitableFloorFamily'),
            ean: JsonDecoder.string,
            properties: JsonDecoder.array(
                JsonDecoder.object(
                    {
                        label: JsonDecoder.string,
                        value: JsonDecoder.string,
                    },
                    'Property'
                ),
                'Properties'
            ),
            filter: JsonDecoder.object<AccessoryFilter>(
                {
                    subsoil: JsonDecoder.array<Subsoil>(
                        JsonDecoder.oneOf<Subsoil>(
                            [
                                JsonDecoder.isExactly('wood'),
                                JsonDecoder.isExactly('screed'),
                                JsonDecoder.isExactly('tiles'),
                                JsonDecoder.isExactly('other'),
                            ],
                            'subsoil'
                        ),
                        'subsoil'
                    ),
                    requirements: JsonDecoder.array<Requirement>(
                        JsonDecoder.oneOf<Requirement>(
                            [
                                JsonDecoder.isExactly('unevenSubsoil'),
                                JsonDecoder.isExactly('soundproofing'),
                                JsonDecoder.isExactly('thermalInsulation'),
                                JsonDecoder.isExactly('underfloorHeating'),
                                JsonDecoder.isExactly('vaporBarrier'),
                                JsonDecoder.isExactly('stress'),
                            ],
                            'requirement'
                        ),
                        'requirements'
                    ),
                },
                'Filter'
            ),
        },
        'Insulations'
    ),
    'Insulation'
);

const skirtingsDecoder = JsonDecoder.array<Skirting>(
    JsonDecoder.object(
        {
            label: JsonDecoder.string,
            imageUri: JsonDecoder.nullable(JsonDecoder.string),
            skirtingId: JsonDecoder.string,
            dimensions: JsonDecoder.object(
                { height: JsonDecoder.number, width: JsonDecoder.number, length: JsonDecoder.number },
                'dimensions'
            ),
            decors: JsonDecoder.array<SkirtingDecor>(
                JsonDecoder.object(
                    {
                        articleNumber: JsonDecoder.string,
                        decorNumbers: JsonDecoder.array(JsonDecoder.string, 'decorNumber'),
                        ean: JsonDecoder.string,
                        imageUri: JsonDecoder.nullable(JsonDecoder.string),
                        label: JsonDecoder.string,
                        pricePerPiece: JsonDecoder.number,
                        isMono: JsonDecoder.boolean,
                    },
                    'InstallationAndCare'
                ),
                'skirtingDecors'
            ),
        },
        'Skirting'
    ),
    'Skirtings'
);

const installationAndCareDecoder = JsonDecoder.array<InstallationAndCare>(
    JsonDecoder.object(
        {
            label: JsonDecoder.string,
            imageUri: JsonDecoder.nullable(JsonDecoder.string),
            accessoryId: JsonDecoder.string,
            variants: JsonDecoder.array<InstallationAndCareVariant>(
                JsonDecoder.object(
                    {
                        articleNumber: JsonDecoder.string,
                        ean: JsonDecoder.string,
                        imageUri: JsonDecoder.nullable(JsonDecoder.string),
                        label: JsonDecoder.string,
                        pricePerPiece: JsonDecoder.number,
                    },
                    'InstallationAndCareVariant'
                ),
                'InstallationAndCareVariants'
            ),
            suitableFloorFamilies: JsonDecoder.array(JsonDecoder.string, 'suitableFloorFamily'),
        },
        'InstallationAndCare'
    ),
    'InstallationAndCareItems'
);

const accessoriesDataResponseDecoder = JsonDecoder.object<AccessoriesDataResponse>(
    {
        data: JsonDecoder.object(
            {
                insulations: insulationsDecoder,
                skirtings: skirtingsDecoder,
                installationAndCare: installationAndCareDecoder,
            },
            'AccessoriesDataResponseBody'
        ),
    },
    'AccessoriesDataResponse'
);
